import { Patient, DepartmentMetric, OperationalAlert, ClinicalDocument, Resident, RoomAmbientSensor, HomeRPMSenior, EarlyDeteriorationSignal, AmbientRoom, ClinicalBusiness, UserProfile } from './types';

export const INITIAL_CLINICAL_BUSINESSES: ClinicalBusiness[] = [
  {
    id: 'biz-hospital-01',
    name: 'Metropolitan Memorial Trauma Hub',
    slug: 'metro-memorial',
    facilityType: 'Tertiary Trauma Center',
    licenseTier: 'ENTERPRISE_UNIFIED',
    licenseKey: 'MC-ENT-2025-9844-ACTIVE',
    activeNpi: '1942008412',
    authorizedMaxBeds: 250,
    featuresUnlocked: {
      cptBillingLedger: true,
      digitalShaSignature: true,
      voiceBiomarkersDSP: true,
      ambientRadarEmergency: true,
      drillSimulations: true
    }
  },
  {
    id: 'biz-senior-02',
    name: 'Oakridge Memory Care & Senior Cottages',
    slug: 'oakridge-memory',
    facilityType: 'Senior Living & Memory Care',
    licenseTier: 'COMMERCIAL_PRO',
    licenseKey: 'MC-PRO-CARE-7719-VALID',
    activeNpi: '1482991055',
    authorizedMaxBeds: 60,
    featuresUnlocked: {
      cptBillingLedger: true,
      digitalShaSignature: false,
      voiceBiomarkersDSP: true,
      ambientRadarEmergency: true,
      drillSimulations: true
    }
  },
  {
    id: 'biz-telehealth-03',
    name: 'Baystate Community RPM & PACE Network',
    slug: 'baystate-pace',
    facilityType: 'PACE Community RPM Network',
    licenseTier: 'BETA_TRIAL',
    trialDaysRemaining: 14,
    licenseKey: 'MC-TRIAL-30D-EXP-14D',
    activeNpi: '1092841029',
    authorizedMaxBeds: 25,
    featuresUnlocked: {
      cptBillingLedger: false,
      digitalShaSignature: false,
      voiceBiomarkersDSP: true,
      ambientRadarEmergency: true,
      drillSimulations: false
    }
  }
];

export const INITIAL_USERS: UserProfile[] = [
  {
    id: 'usr-sterling-01',
    fullName: 'Dr. Marcus Sterling, MD',
    email: 'm.sterling@medicore.health',
    role: 'CHIEF_MEDICAL_OFFICER',
    title: 'Chief Medical Officer & VP of Clinical Operations',
    avatarInitials: 'MS',
    clinicalBusinessId: 'biz-hospital-01',
    pinRequired: true,
    permissions: {
      canSignClinicalDocs: true,
      canTriggerDrills: true,
      canPlaceLabOrders: true,
      canSendFamilySMS: true,
      canManageBilling: true,
      canConfigureSensors: true
    }
  },
  {
    id: 'usr-lin-02',
    fullName: 'Dr. Sarah Lin, FACC',
    email: 's.lin@medicore.health',
    role: 'ATTENDING_PHYSICIAN',
    title: 'Attending Cardiologist & Inpatient Lead',
    avatarInitials: 'SL',
    clinicalBusinessId: 'biz-hospital-01',
    pinRequired: true,
    permissions: {
      canSignClinicalDocs: true,
      canTriggerDrills: false,
      canPlaceLabOrders: true,
      canSendFamilySMS: true,
      canManageBilling: false,
      canConfigureSensors: true
    }
  },
  {
    id: 'usr-novak-03',
    fullName: 'Elena Rostova, RN',
    email: 'e.rostova@oakridge-care.org',
    role: 'STAFF_CAREGIVER',
    title: 'Senior Living Charge Nurse & ADL Coordinator',
    avatarInitials: 'ER',
    clinicalBusinessId: 'biz-senior-02',
    pinRequired: false,
    permissions: {
      canSignClinicalDocs: false,
      canTriggerDrills: true,
      canPlaceLabOrders: true,
      canSendFamilySMS: true,
      canManageBilling: false,
      canConfigureSensors: false
    }
  },
  {
    id: 'usr-vance-04',
    fullName: 'Arthur Vance, MBA',
    email: 'a.vance@baystate-pace.gov',
    role: 'BILLING_COMPLIANCE_DIRECTOR',
    title: 'Medicare Revenue Cycle & Compliance Officer',
    avatarInitials: 'AV',
    clinicalBusinessId: 'biz-telehealth-03',
    pinRequired: false,
    permissions: {
      canSignClinicalDocs: false,
      canTriggerDrills: false,
      canPlaceLabOrders: false,
      canSendFamilySMS: false,
      canManageBilling: true,
      canConfigureSensors: false
    }
  }
];

export const INITIAL_AMBIENT_ROOMS: AmbientRoom[] = [
  {
    roomId: '101',
    roomNumber: 'Room 101',
    residentName: 'Eleanor Vance',
    age: 84,
    diagnosis: 'Post-Op Knee Arthroplasty & Osteoporosis',
    wing: 'Wing-A Transitional Rehab',
    posture: 'Bed Resting',
    bedState: 'In Bed',
    bedTimeMinutes: 145,
    bedTimeLabel: 'In bed: 2 hrs 25 mins',
    bathroomVisitsToday: 3,
    lastBathroomTime: '1 hr ago',
    gaitSpeedMs: 1.05,
    gaitStatus: 'Normal',
    activeAlert: 'None',
    familyContact: {
      name: 'Sarah Vance',
      relation: 'Daughter',
      phone: '(617) 555-0144'
    },
    rpmTelemetry: {
      cpt99454Days: 19,
      targetDays: 16,
      reimbursementAmount: 118,
      isQualified: true,
      daysToThreshold: 0,
      cellularBP: '128/82 mmHg',
      pulseBpm: 72,
      weightLbs: 142.4,
      weightDelta48hLbs: 0.3,
      fluidRetentionAlert: false,
      lastTransmissionTime: 'Today 08:15 AM'
    },
    adlActivity: {
      fridgeOpened: 'Opened 8:15 AM (Breakfast Confirmed)',
      medCabinetOpened: 'Opened 9:02 AM (Dose Adherent)',
      fridgeAlert: false
    },
    morningWellnessCall: {
      callTimestamp: '8:30 AM',
      duration: '1m 45s',
      status: 'Completed',
      sentimentMood: 'Tired but in good spirits',
      vocalEnergyPct: 84,
      vocalEnergyLabel: 'Normal: 84%',
      speechLatencySecs: 1.8,
      speechLatencyLabel: 'Mild Delay - 1.8s',
      jitterShimmerIndex: '0.88% (Normal Fundamental Perturbation)',
      speechRateWpm: 104,
      transcriptSnippet: [
        {
          speaker: 'AI Voice Agent',
          text: 'Good morning Eleanor, how is your left knee feeling after physical therapy yesterday?'
        },
        {
          speaker: 'Eleanor',
          text: 'A little stiff when I got up, but better once I took my morning tea.'
        }
      ]
    }
  },
  {
    roomId: '102',
    roomNumber: 'Room 102',
    residentName: 'Julian Hayes',
    age: 79,
    diagnosis: 'Parkinsonian Gait & Nocturia',
    wing: 'Wing-B Memory Care',
    posture: 'Walking',
    bedState: 'Out of Bed',
    bedTimeMinutes: 4,
    bedTimeLabel: 'Out of bed: 4 mins',
    bathroomVisitsToday: 4,
    lastBathroomTime: '25 mins ago',
    gaitSpeedMs: 0.72,
    gaitStatus: 'Slowing (High Fall Risk)',
    activeAlert: 'None',
    familyContact: {
      name: 'Emily Hayes',
      relation: 'Spouse',
      phone: '(617) 555-7820'
    },
    rpmTelemetry: {
      cpt99454Days: 14,
      targetDays: 16,
      reimbursementAmount: 118,
      isQualified: false,
      daysToThreshold: 2,
      cellularBP: '134/86 mmHg',
      pulseBpm: 76,
      weightLbs: 178.6,
      weightDelta48hLbs: 0.5,
      fluidRetentionAlert: false,
      lastTransmissionTime: 'Today 07:50 AM'
    },
    adlActivity: {
      fridgeOpened: 'Opened 8:15 AM (Breakfast Confirmed)',
      medCabinetOpened: 'Opened 9:02 AM (Dose Adherent)',
      fridgeAlert: false
    },
    morningWellnessCall: {
      callTimestamp: '8:45 AM',
      duration: '1m 20s',
      status: 'Completed',
      sentimentMood: 'Calm & Cooperative',
      vocalEnergyPct: 76,
      vocalEnergyLabel: 'Mild Fatigue: 76%',
      speechLatencySecs: 2.1,
      speechLatencyLabel: 'Hesitation Delay - 2.1s',
      jitterShimmerIndex: '0.94% (Acoustic Tremor Detected)',
      speechRateWpm: 94,
      transcriptSnippet: [
        {
          speaker: 'AI Voice Agent',
          text: 'Good morning Julian! Are you ready for courtyard coffee with the walking group today?'
        },
        {
          speaker: 'Julian',
          text: 'Morning. Took a moment to find my balance by the dresser, but I would like that.'
        }
      ]
    }
  },
  {
    roomId: '103',
    roomNumber: 'Room 103',
    residentName: 'Robert Chen',
    age: 88,
    diagnosis: 'Mild Cognitive Impairment & Stage 3 CKD',
    wing: 'Wing-C Assisted Living',
    posture: 'Standing',
    bedState: 'Out of Bed',
    bedTimeMinutes: 18,
    bedTimeLabel: 'Out of bed: 18 mins',
    bathroomVisitsToday: 7,
    lastBathroomTime: '12 mins ago',
    gaitSpeedMs: 0.68,
    gaitStatus: 'Slowing (High Fall Risk)',
    activeAlert: 'None',
    alertTimestamp: 'Today 09:15 AM',
    aiSbarNote: 'SBAR HANDOFF:\nSituation: Resident Robert Chen in Room 103 logged 7 bathroom voiding events today (>6 cutoff) with slowing gait (0.68 m/s).\nBackground: History of recurrent cystitis and mild cognitive decline.\nAssessment: Impending urinary tract infection and acute dehydration resulting in orthostatic fall vulnerability.\nRecommendation: STAT dipstick urinalysis, immediate nursing oral hydration protocol (8 oz q2h), and activate bed presence boundary audio chime.',
    familyContact: {
      name: 'David Chen',
      relation: 'Son',
      phone: '(617) 555-4819'
    },
    rpmTelemetry: {
      cpt99454Days: 16,
      targetDays: 16,
      reimbursementAmount: 118,
      isQualified: true,
      daysToThreshold: 0,
      cellularBP: '142/88 mmHg',
      pulseBpm: 82,
      weightLbs: 166.2,
      weightDelta48hLbs: 2.8,
      fluidRetentionAlert: true,
      lastTransmissionTime: 'Today 08:30 AM'
    },
    adlActivity: {
      fridgeOpened: 'Inactive since 6:00 PM yesterday (Missed Meal Warning)',
      medCabinetOpened: 'Opened 9:02 AM (Dose Adherent)',
      fridgeAlert: true
    },
    morningWellnessCall: {
      callTimestamp: '8:15 AM',
      duration: '2m 10s',
      status: 'Completed',
      sentimentMood: 'Weary / Disoriented',
      vocalEnergyPct: 62,
      vocalEnergyLabel: 'Fatigued: 62%',
      speechLatencySecs: 2.7,
      speechLatencyLabel: 'Cognitive Hesitation - 2.7s',
      jitterShimmerIndex: '1.24% (Significant Perturbation)',
      speechRateWpm: 86,
      transcriptSnippet: [
        {
          speaker: 'AI Voice Agent',
          text: 'Good morning Robert! Checking in on how you slept through the night.'
        },
        {
          speaker: 'Robert',
          text: 'Not very well... had to get up multiple times for the bathroom. My mouth feels so dry.'
        }
      ]
    }
  }
];

export const INITIAL_PATIENTS: Patient[] = [
  {
    id: 'p-101',
    mrn: 'MC-84920-E',
    fullName: 'Eleanor Vance',
    age: 64,
    gender: 'Female',
    bloodType: 'O+',
    admissionDate: '2025-05-10 06:45',
    department: 'Intensive Care',
    bedNumber: 'ICU-B04',
    attendingPhysician: 'Dr. Marcus Sterling, MD',
    status: 'Critical',
    triageScore: 1,
    primaryDiagnosis: 'Acute Coronary Syndrome & Cardiogenic Shock',
    allergies: ['Penicillin', 'Sulfa drugs'],
    vitals: {
      heartRate: 118,
      bpSystolic: 88,
      bpDiastolic: 54,
      spO2: 92,
      tempC: 38.2,
      respRate: 26
    },
    familyContact: {
      name: 'Sarah Vance',
      relation: 'Daughter',
      phone: '(617) 555-0144'
    }
  },
  {
    id: 'p-102',
    mrn: 'MC-91024-C',
    fullName: 'Julian Hayes',
    age: 48,
    gender: 'Male',
    bloodType: 'A-',
    admissionDate: '2025-05-11 11:20',
    department: 'Cardiology',
    bedNumber: 'CARD-208',
    attendingPhysician: 'Dr. Sarah Lin, FACC',
    status: 'Guarded',
    triageScore: 2,
    primaryDiagnosis: 'Atrial Fibrillation with Rapid Ventricular Response',
    allergies: ['Latex'],
    vitals: {
      heartRate: 134,
      bpSystolic: 138,
      bpDiastolic: 86,
      spO2: 97,
      tempC: 36.9,
      respRate: 19
    },
    familyContact: {
      name: 'Emily Hayes',
      relation: 'Spouse',
      phone: '(617) 555-7820'
    }
  },
  {
    id: 'p-103',
    mrn: 'MC-72991-N',
    fullName: 'Samantha Rivera',
    age: 32,
    gender: 'Female',
    bloodType: 'B+',
    admissionDate: '2025-05-12 02:15',
    department: 'Neurology',
    bedNumber: 'NEUR-112',
    attendingPhysician: 'Dr. Alan Vance, MD',
    status: 'Stable',
    triageScore: 3,
    primaryDiagnosis: 'Intractable Migraine with Aura & Atypical Neuropathy',
    allergies: ['Aspirin', 'Ibuprofen'],
    vitals: {
      heartRate: 74,
      bpSystolic: 122,
      bpDiastolic: 78,
      spO2: 99,
      tempC: 36.7,
      respRate: 16
    },
    familyContact: {
      name: 'Carlos Rivera',
      relation: 'Brother',
      phone: '(617) 555-9311'
    }
  },
  {
    id: 'p-104',
    mrn: 'MC-64819-E',
    fullName: 'Arthur Pendelton',
    age: 72,
    gender: 'Male',
    bloodType: 'AB+',
    admissionDate: '2025-05-09 14:30',
    department: 'Emergency',
    bedNumber: 'ER-BAY-09',
    attendingPhysician: 'Dr. David Chen, MD',
    status: 'Discharge Ready',
    triageScore: 4,
    primaryDiagnosis: 'Mild Exacerbation of COPD, Resolved',
    allergies: ['No Known Drug Allergies'],
    vitals: {
      heartRate: 76,
      bpSystolic: 128,
      bpDiastolic: 80,
      spO2: 98,
      tempC: 36.6,
      respRate: 15
    },
    familyContact: {
      name: 'Margaret Pendelton',
      relation: 'Wife',
      phone: '(617) 555-3209'
    }
  }
];

export const INITIAL_RESIDENTS: Resident[] = [
  {
    id: 'res-101',
    roomNumber: 'Room 101 (Wing-A)',
    fullName: 'Eleanor Vance',
    preferredName: 'Eleanor',
    age: 84,
    gender: 'Female',
    codeStatus: 'FULL CODE',
    primaryDiagnosis: 'Post-Op Knee Arthroplasty & Severe Osteoporosis',
    mobilityTransfer: '1-Person Assist',
    fallRisk: 'High (Bed Alarm On)',
    fallRiskScoreMorse: 85,
    bedAlarmActive: true,
    dietaryTexture: 'Mechanical Soft',
    fluidConsistency: 'Nectar-Thick',
    lastTurnTimestamp: '08:30 AM',
    turnMinutesRemaining: 25,
    turnSide: 'Supine',
    primaryEmergencyContact: {
      name: 'Sarah Vance',
      relation: 'Daughter / Family Proxy',
      phone: '(617) 555-0144'
    },
    dailyLog: {
      mood: 'Cheerful, completed morning wellness check-in, doing physical therapy',
      activity: 'Assisted ambulation and knee range-of-motion protocol',
      lunchIntakePct: 90,
      hydrationOz: 42,
      restPeriods: 'Afternoon rest in bed with leg elevated'
    },
    vitals: {
      heartRate: 74,
      bpSystolic: 128,
      bpDiastolic: 82,
      spO2: 97,
      tempC: 36.8
    }
  },
  {
    id: 'res-301',
    roomNumber: 'Wing-B #204',
    fullName: 'Dorothy G. Miller',
    preferredName: 'Dottie',
    age: 87,
    gender: 'Female',
    codeStatus: 'DNR',
    primaryDiagnosis: 'Advanced Vascular Dementia & Right Hip Hemiarthroplasty',
    mobilityTransfer: 'Hoyer Lift',
    fallRisk: 'High (Bed Alarm On)',
    fallRiskScoreMorse: 85,
    bedAlarmActive: true,
    dietaryTexture: 'Pureed',
    fluidConsistency: 'Honey-Thick',
    lastTurnTimestamp: '08:45 AM',
    turnMinutesRemaining: 18,
    turnSide: 'Left Lateral',
    primaryEmergencyContact: {
      name: 'Carolyn Miller',
      relation: 'Daughter / Health Proxy',
      phone: '(617) 555-8321'
    },
    dailyLog: {
      mood: 'Bright and smiling, hummed along to Frank Sinatra in courtyard',
      activity: 'Sensory gardening session and upper body range of motion',
      lunchIntakePct: 85,
      hydrationOz: 36,
      restPeriods: 'Took restful 45-min afternoon nap after lunch'
    },
    vitals: {
      heartRate: 72,
      bpSystolic: 118,
      bpDiastolic: 70,
      spO2: 97,
      tempC: 36.8
    }
  },
  {
    id: 'res-302',
    roomNumber: 'Wing-A #112',
    fullName: 'Harold J. Greenfield',
    preferredName: 'Hal',
    age: 91,
    gender: 'Male',
    codeStatus: 'POLST on File',
    primaryDiagnosis: 'Parkinsonian Tremors & Chronic Orthostatic Hypotension',
    mobilityTransfer: '2-Person Assist',
    fallRisk: 'High (Bed Alarm On)',
    fallRiskScoreMorse: 75,
    bedAlarmActive: true,
    dietaryTexture: 'Mechanical Soft',
    fluidConsistency: 'Nectar-Thick',
    lastTurnTimestamp: '07:15 AM',
    turnMinutesRemaining: 0,
    turnSide: 'Supine',
    primaryEmergencyContact: {
      name: 'Robert Greenfield',
      relation: 'Son / Durable POA',
      phone: '(617) 555-9042'
    },
    dailyLog: {
      mood: 'Calm, engaged in current events discussion during coffee hour',
      activity: 'Physical therapy gait training with parallel bars (15 mins)',
      lunchIntakePct: 70,
      hydrationOz: 40,
      restPeriods: 'Rested comfortably in high-back recliner'
    },
    vitals: {
      heartRate: 68,
      bpSystolic: 112,
      bpDiastolic: 68,
      spO2: 96,
      tempC: 36.6
    }
  },
  {
    id: 'res-303',
    roomNumber: 'Wing-C #308',
    fullName: 'Evelyn Rose Sterling',
    preferredName: 'Evie',
    age: 82,
    gender: 'Female',
    codeStatus: 'FULL CODE',
    primaryDiagnosis: 'Severe Osteoarthritis & Mild Cognitive Impairment',
    mobilityTransfer: '1-Person Assist',
    fallRisk: 'Moderate',
    fallRiskScoreMorse: 45,
    bedAlarmActive: false,
    dietaryTexture: 'Regular',
    fluidConsistency: 'Thin Liquids',
    lastTurnTimestamp: '09:30 AM',
    turnMinutesRemaining: 68,
    turnSide: 'Right Lateral',
    primaryEmergencyContact: {
      name: 'Brian Sterling',
      relation: 'Son',
      phone: '(617) 555-4411'
    },
    dailyLog: {
      mood: 'Very sociable, won two rounds of morning bingo with peers',
      activity: 'Watercolor painting studio and social dining group',
      lunchIntakePct: 100,
      hydrationOz: 48,
      restPeriods: 'Quiet reading in sunroom between 1:00 PM and 2:00 PM'
    },
    vitals: {
      heartRate: 78,
      bpSystolic: 126,
      bpDiastolic: 76,
      spO2: 98,
      tempC: 36.7
    }
  },
  {
    id: 'res-304',
    roomNumber: 'Wing-B #216',
    fullName: 'Walter Anthony Kowalski',
    preferredName: 'Walt',
    age: 88,
    gender: 'Male',
    codeStatus: 'DNR',
    primaryDiagnosis: 'Post-CVA Left Hemiparesis & Stage II Sacral Ulcer',
    mobilityTransfer: 'Hoyer Lift',
    fallRisk: 'High (Bed Alarm On)',
    fallRiskScoreMorse: 90,
    bedAlarmActive: true,
    dietaryTexture: 'Pureed',
    fluidConsistency: 'Honey-Thick',
    lastTurnTimestamp: '09:50 AM',
    turnMinutesRemaining: 95,
    turnSide: 'Left Lateral',
    primaryEmergencyContact: {
      name: 'Theresa Kowalski',
      relation: 'Daughter / Proxy',
      phone: '(617) 555-2389'
    },
    dailyLog: {
      mood: 'Peaceful, enjoyed listening to audio classical book',
      activity: 'Bedside passive range of motion and skin barrier assessment',
      lunchIntakePct: 60,
      hydrationOz: 32,
      restPeriods: 'Regular 2-hour q2h repositioning protocol maintained'
    },
    vitals: {
      heartRate: 74,
      bpSystolic: 130,
      bpDiastolic: 74,
      spO2: 95,
      tempC: 37.0
    }
  }
];

export const INITIAL_ROOM_SENSORS: RoomAmbientSensor[] = [
  {
    roomId: 'room-112',
    roomName: 'Suite 112',
    wing: 'Wing-A Memory Care',
    residentName: 'Harold J. Greenfield',
    residentAge: 91,
    residentId: 'res-302',
    mmWaveRadar: {
      status: 'Fall Warning',
      posture: 'Floor Level (Alert)',
      signalDb: -52,
      lastDetected: '3 mins ago'
    },
    mattressSleepSensor: {
      state: 'Out of Bed',
      respirationRate: 22,
      heartRateMicro: 84,
      sleepQualityPct: 62,
      durationOutOfBedMins: 28
    },
    bathroomTracker: {
      visitsToday: 8,
      lastVisitTime: '22 mins ago',
      avgDurationMins: 11.4,
      utiRiskAlert: true
    },
    familyContact: {
      name: 'Robert Greenfield',
      relation: 'Son',
      phone: '(617) 555-9042'
    }
  },
  {
    roomId: 'room-204',
    roomName: 'Suite 204',
    wing: 'Wing-B Assisted',
    residentName: 'Dorothy G. Miller',
    residentAge: 87,
    residentId: 'res-301',
    mmWaveRadar: {
      status: 'Normal',
      posture: 'Seated',
      signalDb: -38,
      lastDetected: 'Just now'
    },
    mattressSleepSensor: {
      state: 'Out of Bed',
      respirationRate: 16,
      heartRateMicro: 72,
      sleepQualityPct: 88,
      durationOutOfBedMins: 45
    },
    bathroomTracker: {
      visitsToday: 3,
      lastVisitTime: '1 hr ago',
      avgDurationMins: 4.8,
      utiRiskAlert: false
    },
    familyContact: {
      name: 'Carolyn Miller',
      relation: 'Daughter',
      phone: '(617) 555-8321'
    }
  },
  {
    roomId: 'room-308',
    roomName: 'Suite 308',
    wing: 'Wing-C Rehab',
    residentName: 'Evelyn Rose Sterling',
    residentAge: 82,
    residentId: 'res-303',
    mmWaveRadar: {
      status: 'Normal',
      posture: 'Walking',
      signalDb: -32,
      lastDetected: 'Just now'
    },
    mattressSleepSensor: {
      state: 'Out of Bed',
      respirationRate: 15,
      heartRateMicro: 76,
      sleepQualityPct: 91,
      durationOutOfBedMins: 110
    },
    bathroomTracker: {
      visitsToday: 4,
      lastVisitTime: '2 hrs ago',
      avgDurationMins: 3.5,
      utiRiskAlert: false
    },
    familyContact: {
      name: 'Brian Sterling',
      relation: 'Son',
      phone: '(617) 555-4411'
    }
  },
  {
    roomId: 'room-216',
    roomName: 'Suite 216',
    wing: 'Wing-B Assisted',
    residentName: 'Walter Anthony Kowalski',
    residentAge: 88,
    residentId: 'res-304',
    mmWaveRadar: {
      status: 'Normal',
      posture: 'Standing',
      signalDb: -41,
      lastDetected: '1 min ago'
    },
    mattressSleepSensor: {
      state: 'In Bed',
      respirationRate: 17,
      heartRateMicro: 70,
      sleepQualityPct: 84,
      durationOutOfBedMins: 0
    },
    bathroomTracker: {
      visitsToday: 7,
      lastVisitTime: '45 mins ago',
      avgDurationMins: 8.2,
      utiRiskAlert: true
    },
    familyContact: {
      name: 'Theresa Kowalski',
      relation: 'Daughter',
      phone: '(617) 555-2389'
    }
  }
];

export const INITIAL_HOME_RPM_SENIORS: HomeRPMSenior[] = [
  {
    id: 'rpm-401',
    fullName: 'Clara M. Abernathy',
    age: 79,
    city: 'Brookline, MA (Residence)',
    enrolledProgram: 'CHF Tele-Care',
    cellularBP: {
      systolic: 122,
      diastolic: 78,
      pulse: 74,
      timestamp: 'Today 07:45 AM',
      status: 'Optimal'
    },
    cellularScale: {
      weightLbs: 168.4,
      trend7DayLbs: 0.2,
      timestamp: 'Today 07:50 AM',
      rapidGainFlag: false
    },
    adlActivity: {
      fridgeOpensToday: 5,
      lastFridgeTime: '08:30 AM (Breakfast)',
      medCabinetOpensToday: 2,
      medComplianceStatus: 'Taken',
      overallAdlScore: 88
    },
    cpt99454: {
      daysLoggedThisMonth: 18,
      targetDays: 16,
      isCompliantReimbursable: true,
      estimatedBillingAmount: 52.48,
      daysRemainingInCycle: 12
    },
    primaryCarePhysician: 'Dr. Rebecca Foster, MD',
    nurseTriageNotes: 'Routine morning transmission completed with cellular cuff and scale. All readings stable.',
    familyContact: {
      name: 'Sarah Abernathy',
      relation: 'Daughter',
      phone: '(617) 555-6670'
    }
  },
  {
    id: 'rpm-402',
    fullName: 'Raymond Francis Cole',
    age: 84,
    city: 'Newton, MA (Residence)',
    enrolledProgram: 'Hypertension Remote Guard',
    cellularBP: {
      systolic: 124,
      diastolic: 78,
      pulse: 70,
      timestamp: 'Today 08:10 AM',
      status: 'Optimal'
    },
    cellularScale: {
      weightLbs: 182.0,
      trend7DayLbs: -0.4,
      timestamp: 'Today 08:15 AM',
      rapidGainFlag: false
    },
    adlActivity: {
      fridgeOpensToday: 7,
      lastFridgeTime: '12:40 PM',
      medCabinetOpensToday: 2,
      medComplianceStatus: 'Taken',
      overallAdlScore: 94
    },
    cpt99454: {
      daysLoggedThisMonth: 21,
      targetDays: 16,
      isCompliantReimbursable: true,
      estimatedBillingAmount: 52.48,
      daysRemainingInCycle: 9
    },
    primaryCarePhysician: 'Dr. Marcus Sterling, MD',
    nurseTriageNotes: 'Excellent adherence. Daily readings consistently uploaded before 08:30 AM via LTE cellular hub. No interventions required.',
    familyContact: {
      name: 'David Cole',
      relation: 'Son',
      phone: '(617) 555-4819'
    }
  },
  {
    id: 'rpm-403',
    fullName: 'Gladys Irene Thorne',
    age: 86,
    city: 'Cambridge, MA (Residence)',
    enrolledProgram: 'Diabetic Frailty',
    cellularBP: {
      systolic: 118,
      diastolic: 72,
      pulse: 68,
      timestamp: 'Today 08:00 AM',
      status: 'Optimal'
    },
    cellularScale: {
      weightLbs: 134.2,
      trend7DayLbs: -0.2,
      timestamp: 'Today 08:05 AM',
      rapidGainFlag: false
    },
    adlActivity: {
      fridgeOpensToday: 4,
      lastFridgeTime: '08:45 AM (Breakfast)',
      medCabinetOpensToday: 1,
      medComplianceStatus: 'Taken',
      overallAdlScore: 82
    },
    cpt99454: {
      daysLoggedThisMonth: 12,
      targetDays: 16,
      isCompliantReimbursable: false,
      estimatedBillingAmount: 0.00,
      daysRemainingInCycle: 5
    },
    primaryCarePhysician: 'Dr. Sarah Lin, FACC',
    nurseTriageNotes: 'Morning transmission logged. Blood pressure and weight in target zone.',
    familyContact: {
      name: 'Jessica Thorne',
      relation: 'Granddaughter',
      phone: '(617) 555-9128'
    }
  },
  {
    id: 'rpm-404',
    fullName: 'Douglas K. MacIntyre',
    age: 81,
    city: 'Somerville, MA (Residence)',
    enrolledProgram: 'Post-Stroke Recovery',
    cellularBP: {
      systolic: 130,
      diastolic: 80,
      pulse: 72,
      timestamp: 'Today 09:00 AM',
      status: 'Optimal'
    },
    cellularScale: {
      weightLbs: 174.5,
      trend7DayLbs: 0.2,
      timestamp: 'Today 09:05 AM',
      rapidGainFlag: false
    },
    adlActivity: {
      fridgeOpensToday: 5,
      lastFridgeTime: '08:20 AM',
      medCabinetOpensToday: 2,
      medComplianceStatus: 'Taken',
      overallAdlScore: 88
    },
    cpt99454: {
      daysLoggedThisMonth: 17,
      targetDays: 16,
      isCompliantReimbursable: true,
      estimatedBillingAmount: 52.48,
      daysRemainingInCycle: 13
    },
    primaryCarePhysician: 'Dr. Alan Vance, MD',
    nurseTriageNotes: 'Mobility ADL score stable. Fridge activity patterns match baseline morning breakfast and lunch routines.',
    familyContact: {
      name: 'Heather MacIntyre',
      relation: 'Daughter',
      phone: '(617) 555-7733'
    }
  }
];

export const INITIAL_EARLY_DETERIORATION_SIGNALS: EarlyDeteriorationSignal[] = [
  {
    id: 'sentry-101',
    seniorId: 'res-302',
    seniorName: 'Harold J. Greenfield',
    location: 'Suite 112 (Wing-A Memory Care)',
    detectedAt: '12 mins ago',
    trends: [
      {
        label: 'Gait / Walking Speed',
        delta: 'Slowed by 15.4%',
        baseline: '0.82 m/s',
        current: '0.69 m/s',
        severity: 'warning'
      },
      {
        label: 'Bathroom Frequency',
        delta: 'Doubled (2.4x baseline)',
        baseline: '3.5 visits/day',
        current: '8 visits today',
        severity: 'critical'
      },
      {
        label: 'Resting Heart Rate',
        delta: 'Increased by +8 bpm',
        baseline: '68 bpm',
        current: '76 bpm',
        severity: 'elevated'
      }
    ],
    aiInterventionNote: 'Suspected early UTI or delirium risk. Recommend urinalysis and hydration check before fall occurs. Sub-visual gait hesitations and elevated nighttime restless voiding indicate developing autonomic irritation.',
    riskSuspicions: ['Early Urinary Tract Infection (UTI)', 'Impending Fall Hazard', 'Prodromal Delirium'],
    orderPlaced: false
  },
  {
    id: 'sentry-102',
    seniorId: 'rpm-401',
    seniorName: 'Clara M. Abernathy',
    location: 'Brookline, MA (Residence)',
    detectedAt: '28 mins ago',
    trends: [
      {
        label: 'Cellular Scale Weight',
        delta: '+3.8 lbs in 48 hours',
        baseline: '164.6 lbs',
        current: '168.4 lbs',
        severity: 'critical'
      },
      {
        label: 'ADL Medication Access',
        delta: 'Morning dose sensor unprompted',
        baseline: 'Daily by 08:30 AM',
        current: '0 cabinet opens',
        severity: 'warning'
      },
      {
        label: 'Resting Pulse Telemetry',
        delta: 'Elevated +7 bpm',
        baseline: '75 bpm',
        current: '82 bpm',
        severity: 'elevated'
      }
    ],
    aiInterventionNote: 'Suspected acute decompensated heart failure with impending pulmonary congestion. Recommend immediate nurse outreach call and diuretic adherence verification before emergency escalation.',
    riskSuspicions: ['Acute CHF Exacerbation', 'Diuretic Non-Adherence', 'Dyspnea Hazard'],
    orderPlaced: false
  }
];

export const INITIAL_DEPARTMENT_METRICS: DepartmentMetric[] = [
  { department: 'Intensive Care', occupiedBeds: 28, totalBeds: 30, criticalCases: 14, avgWaitMins: 4, nurseToPatientRatio: '1:1' },
  { department: 'Cardiology', occupiedBeds: 42, totalBeds: 50, criticalCases: 8, avgWaitMins: 18, nurseToPatientRatio: '1:3' },
  { department: 'Emergency', occupiedBeds: 64, totalBeds: 70, criticalCases: 12, avgWaitMins: 32, nurseToPatientRatio: '1:4' },
  { department: 'Neurology', occupiedBeds: 24, totalBeds: 35, criticalCases: 3, avgWaitMins: 22, nurseToPatientRatio: '1:4' },
  { department: 'Oncology', occupiedBeds: 38, totalBeds: 40, criticalCases: 6, avgWaitMins: 15, nurseToPatientRatio: '1:3' }
];

export const INITIAL_ALERTS: OperationalAlert[] = [
  {
    id: 'alt-901',
    timestamp: '10 mins ago',
    level: 'CRITICAL',
    title: 'ICU Bed Surge Warning',
    description: 'ICU Capacity currently at 93.3%. Recommend activating Tier-2 overflow bay in Step-Down wing.',
    department: 'Intensive Care',
    actionRequired: 'Reroute incoming stroke triage to Neuro-ICU Annex',
    resolved: false
  },
  {
    id: 'alt-902',
    timestamp: '25 mins ago',
    level: 'WARNING',
    title: 'Biometric Telemetry Anomaly',
    description: 'SpO2 downward trend detected for Eleanor Vance (ICU-B04) dropping from 96% to 92%.',
    department: 'Intensive Care',
    actionRequired: 'Verify arterial blood gas and increase O2 titration',
    resolved: false
  },
  {
    id: 'alt-903',
    timestamp: '1 hour ago',
    level: 'INFO',
    title: 'Pharmacy Automated Dispense Restock',
    description: 'Stat Vasopressin & Norepinephrine cartridges refilled in Central Tower Pyxis.',
    department: 'Pharmacy',
    actionRequired: 'Routine confirmation logged',
    resolved: true
  }
];

export const SAMPLE_INITIAL_DOC: ClinicalDocument = {
  id: 'DOC-2025-0982',
  patientId: 'p-101',
  patientName: 'Eleanor Vance',
  mrn: 'MC-84920-E',
  author: 'Dr. Marcus Sterling, MD (Chief Medical Officer)',
  date: '2025-05-12 08:30 EST',
  docType: 'SOAP Note',
  chiefComplaint: 'Acute retrosternal chest pressure radiating to left mandible, diaphoresis, and dyspnea at rest.',
  subjective: 'Patient is a 64-year-old female with known history of hypertension and dyslipidemia presenting with sudden onset crushing substernal chest pain starting 3 hours prior to admission. Rated 9/10 in severity. Associated with profound nausea, diaphoresis, and shortness of breath. Denies recent syncope or palpitations.',
  objective: 'Vitals: BP 88/54 mmHg, HR 118 bpm (sinus tachycardia), SpO2 92% on 2L NC, Temp 38.2 C, RR 26/min.\nGeneral: Pale, diaphoretic, in moderate respiratory distress.\nCardiovascular: Tachycardic, S3 gallop audible at apex, no friction rub. Peripheral pulses thready bilaterally.\nPulmonary: Fine bibasilar crackles extending 1/3 up posterior lung fields.\nECG: 3mm ST-elevations in leads II, III, and aVF with reciprocal ST depressions in I and aVL.\nLabs: High-sensitivity Troponin T 1,840 ng/L (Elevated), BNP 920 pg/mL, Lactate 3.1 mmol/L.',
  assessment: '1. Acute Inferior ST-Elevation Myocardial Infarction (STEMI) complicated by Killip Class III Cardiogenic shock.\n2. Lactic acidosis secondary to systemic hypoperfusion.\n3. Sinus tachycardia reactive to acute hemodynamic stress.',
  plan: '1. STAT Cardiology Interventional consultation for emergent Percutaneous Coronary Intervention (PCI).\n2. Continuous arterial line placement and IV Norepinephrine infusion titrated to MAP > 65 mmHg.\n3. Aspirin 325 mg chewed + Ticagrelor 180 mg loading dose administered.\n4. Unfractionated heparin bolus 60 units/kg followed by continuous infusion per protocol.\n5. O2 therapy increased to 4L nasal cannula to maintain SpO2 > 94%.\n6. Serial troponins and 12-lead ECGs q2h.',
  icd10Codes: [
    { code: 'I21.19', description: 'ST elevation myocardial infarction involving other coronary artery of inferior wall' },
    { code: 'R57.0', description: 'Cardiogenic shock' },
    { code: 'R07.9', description: 'Chest pain, unspecified' }
  ],
  prescriptions: [
    { drug: 'Norepinephrine Bitartrate', dosage: '0.05-0.3 mcg/kg/min', frequency: 'Continuous IV Infusion', route: 'Central Line' },
    { drug: 'Ticagrelor', dosage: '90 mg', frequency: 'Twice daily', route: 'Oral' },
    { drug: 'Aspirin', dosage: '81 mg', frequency: 'Daily', route: 'Oral' },
    { drug: 'Atorvastatin Calcium', dosage: '80 mg', frequency: 'Nightly', route: 'Oral' }
  ],
  aiSynthesized: true,
  signed: true,
  signatureDate: '2025-05-12 09:12:44 UTC',
  verificationHash: 'SHA256:7f83b1657ff1fc53b92dc18148a1d65dfc2d4b1fa3d677284addd200126d9069'
};
